'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import omit from 'lodash/omit';
import React, { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';

// UI lib dependencies
import { Box, Modal, ModalProps } from '@mantine/core';

// Store
import dialogStoreVar, { DialogStore } from '../store/dialogStore';

// Types
import {
  DialogIdentifier,
  DialogPropsWildcard,
  DialogProviderProps,
  dialogsList,
} from '../types';

const defaultDialogProps: Partial<ModalProps> = {
  closeOnEscape: true,
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogProvider({ children }: DialogProviderProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const initialStoreState: DialogStore = {
    dialogIdentifier: null,
    invokeDialog: <Identifier extends DialogIdentifier>(
      identifier: Identifier,
      props?: DialogPropsWildcard<Identifier>,
    ) => {
      dialogStoreVar({
        dialogIdentifier: identifier,
        dialogProps: {
          ...props,
          modalProps: { ...defaultDialogProps, ...props?.modalProps },
        },
        closeDialog: (dialogIdentifier: DialogIdentifier) => {
          dialogStoreVar(initialStoreState);
        },
      });
    },
  };

  // Side-effects
  useEffect(
    function onMount() {
      if (
        !dialogStore ||
        (!dialogStore?.invokeDialog && !dialogStore?.closeDialog)
      ) {
        dialogStoreVar(initialStoreState);
      }
    },
    [dialogStore],
  );

  /* ***************************** LOCAL VARIABLES **************************** */

  const showDialog = !!dialogStore && !!dialogStore.dialogIdentifier;
  const Dialog = showDialog
    ? dialogsList[
        (dialogStore as DialogStore).dialogIdentifier as DialogIdentifier
      ]
    : React.Fragment;

  /* ******************************** RENDERING ******************************* */
  return (
    <Box>
      <Modal
        transitionProps={{ duration: 0 }}
        size="auto"
        zIndex={300}
        closeButtonProps={{ size: 'md', c: 'blackroomBlack' }}
        onClose={function cb() {
          dialogStoreVar(initialStoreState);
        }}
        opened={showDialog}
        trapFocus={true}
        centered
        closeOnEscape
        closeOnClickOutside
        {...dialogStore?.dialogProps?.modalProps}
      >
        <div id="blackroom-dialog">
          {dialogStore?.dialogIdentifier ? (
            <Dialog
              {...(showDialog
                ? { ...(omit(dialogStore?.dialogProps, 'modalProps') as any) }
                : {})}
            />
          ) : null}
        </div>
      </Modal>
      {children}
    </Box>
  );
}

export default DialogProvider;
